import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client";
import { EntityLogo } from "../components/EntityLogo";
import {
  useAccessPolicy,
  useAddWhitelist,
  useClearAlliance,
  useClearCorporation,
  useRemoveWhitelist,
  useSetAlliance,
  useSetCorporation,
  type AmbiguousCandidate,
} from "../hooks/useAccessPolicy";

type Kind = "corporation" | "alliance" | "character";

interface PendingPick {
  kind: Kind;
  candidates: AmbiguousCandidate[];
}

export function AccessControl() {
  const policy = useAccessPolicy();
  const setCorp = useSetCorporation();
  const clearCorp = useClearCorporation();
  const setAlly = useSetAlliance();
  const clearAlly = useClearAlliance();
  const addWl = useAddWhitelist();
  const removeWl = useRemoveWhitelist();

  const [corpInput, setCorpInput] = useState("");
  const [allyInput, setAllyInput] = useState("");
  const [wlInput, setWlInput] = useState("");
  const [pending, setPending] = useState<PendingPick | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pickerHeadingId = "access-admin-picker-title";
  const firstCandidateRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (pending) firstCandidateRef.current?.focus();
  }, [pending]);

  const submit = async (kind: Kind, name: string) => {
    setError(null);
    setPending(null);
    try {
      if (kind === "corporation") {
        await setCorp.mutateAsync({ name });
        setCorpInput("");
      } else if (kind === "alliance") {
        await setAlly.mutateAsync({ name });
        setAllyInput("");
      } else {
        await addWl.mutateAsync({ name });
        setWlInput("");
      }
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.code === "ambiguous_name"
      ) {
        const body = err.body as { candidates?: AmbiguousCandidate[] } | null;
        setPending({ kind, candidates: body?.candidates ?? [] });
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(String(err));
      }
    }
  };

  const pick = async (candidate: AmbiguousCandidate) => {
    if (!pending) return;
    setError(null);
    try {
      if (pending.kind === "corporation") {
        await setCorp.mutateAsync({
          corporation_id: candidate.id,
          corporation_name: candidate.name,
        });
      } else if (pending.kind === "alliance") {
        await setAlly.mutateAsync({
          alliance_id: candidate.id,
          alliance_name: candidate.name,
        });
      } else {
        await addWl.mutateAsync({
          character_id: candidate.id,
          character_name: candidate.name,
        });
      }
      setPending(null);
      setCorpInput("");
      setAllyInput("");
      setWlInput("");
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(String(err));
    }
  };

  if (policy.isLoading) return <main className="access-admin">Loading…</main>;
  if (policy.isError || !policy.data)
    return <main className="access-admin">Failed to load access policy.</main>;
  const p = policy.data;

  return (
    <main className="access-admin">
      <a className="access-admin__back" href="#/">← Back</a>
      <h2>Server access</h2>
      <p className="access-admin__intro">
        Only the server owner can edit this. A character may sign in if it
        matches the allowed corporation, the allowed alliance, or the
        whitelist. With nothing set, only the owner can sign in.
      </p>

      <section className="access-admin__section">
        <h3>Allowed corporation</h3>
        {p.allowed_corporation_id != null ? (
          <div className="access-admin__current">
            <span>
              <EntityLogo type="corp" id={p.allowed_corporation_id} />{" "}
              {p.allowed_corporation_name ?? `Corp ${p.allowed_corporation_id}`}
            </span>
            <button type="button" onClick={() => clearCorp.mutate()}>
              Clear
            </button>
          </div>
        ) : (
          <div className="access-admin__current access-admin__current--empty">
            Not set
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit("corporation", corpInput.trim());
          }}
        >
          <input
            value={corpInput}
            onChange={(e) => setCorpInput(e.target.value)}
            placeholder="Corporation name"
          />
          <button type="submit" disabled={corpInput.trim().length < 3}>
            Set
          </button>
        </form>
        {corpInput.trim().length < 3 && (
          <p className="access-admin__hint">Type at least 3 characters</p>
        )}
      </section>

      <section className="access-admin__section">
        <h3>Allowed alliance</h3>
        {p.allowed_alliance_id != null ? (
          <div className="access-admin__current">
            <span>
              <EntityLogo type="alliance" id={p.allowed_alliance_id} />{" "}
              {p.allowed_alliance_name ?? `Alliance ${p.allowed_alliance_id}`}
            </span>
            <button type="button" onClick={() => clearAlly.mutate()}>
              Clear
            </button>
          </div>
        ) : (
          <div className="access-admin__current access-admin__current--empty">
            Not set
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit("alliance", allyInput.trim());
          }}
        >
          <input
            value={allyInput}
            onChange={(e) => setAllyInput(e.target.value)}
            placeholder="Alliance name"
          />
          <button type="submit" disabled={allyInput.trim().length < 3}>
            Set
          </button>
        </form>
        {allyInput.trim().length < 3 && (
          <p className="access-admin__hint">Type at least 3 characters</p>
        )}
      </section>

      <section className="access-admin__section">
        <h3>Character whitelist</h3>
        {p.whitelist.length === 0 ? (
          <div className="access-admin__current access-admin__current--empty">
            No whitelisted characters
          </div>
        ) : (
          <ul className="access-admin__whitelist">
            {p.whitelist.map((w) => (
              <li key={w.character_id}>
                <span>{w.character_name}</span>
                <button
                  type="button"
                  onClick={() => removeWl.mutate(w.character_id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit("character", wlInput.trim());
          }}
        >
          <input
            value={wlInput}
            onChange={(e) => setWlInput(e.target.value)}
            placeholder="Character name"
          />
          <button type="submit" disabled={wlInput.trim().length < 3}>
            Add
          </button>
        </form>
        {wlInput.trim().length < 3 && (
          <p className="access-admin__hint">Type at least 3 characters</p>
        )}
      </section>

      {pending && (
        <div
          className="access-admin__picker"
          role="dialog"
          aria-modal="true"
          aria-labelledby={pickerHeadingId}
        >
          <h4 id={pickerHeadingId}>Multiple matches — pick one</h4>
          <ul>
            {pending.candidates.map((c, idx) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => pick(c)}
                  ref={idx === 0 ? firstCandidateRef : undefined}
                >
                  {pending.kind !== "character" && (
                    <>
                      <EntityLogo
                        type={
                          pending.kind === "corporation" ? "corp" : "alliance"
                        }
                        id={c.id}
                      />{" "}
                    </>
                  )}
                  {c.name}
                  <span className="access-admin__picker-id"> (id {c.id})</span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => setPending(null)}>
            Cancel
          </button>
        </div>
      )}

      {error && (
        <div className="access-admin__error" role="alert">
          {error}
        </div>
      )}
    </main>
  );
}
